import {
  Badge,
  Box,
  Flex,
  HStack,
  IconButton,
  Progress,
  Text,
  Checkbox,
  Tooltip,
  useColorModeValue,
} from "@chakra-ui/react";
import { ViewIcon } from "@chakra-ui/icons";
import { UserMinus, UserCheck, Briefcase, UserRound, CalendarPlus, CalendarCheck, CheckCircle2 } from "lucide-react";
import { formatSp } from "../../../lib/datetime";
import type { Candidate } from "../../../types/onboarding.ts";
import type { WhitelistEntry } from "../../../types/whitelist.ts";
import { UserAvatar } from "../../../components/UserAvatar";
import { ChildrenInteractionBlocker } from "../../../components/overlays/ChildrenInteractionBlocker";
import { PERMISSIONS } from "../../../config/permissions";
import { usePermissionsContext } from "../../../data/permissions/permissionContext";
import { getHrProgress } from "./hrProcessSteps";

interface CandidateCardProps {
  candidate: Candidate;
  onViewDetail?: (candidate: Candidate) => void;
  onCancel?: (candidate: Candidate) => void;
  onReactivate?: (candidate: Candidate) => void;
  isSelected?: boolean;
  onToggleSelect?: (candidateId: string | number) => void;
  showHrProgress?: boolean;
  whitelistEntry?: WhitelistEntry;
}

const STATUS_CONFIG: Record<string, { label: string; colorScheme: string }> = {
  CONCLUIDO:    { label: "Concluído",    colorScheme: "green"  },
  EM_ANDAMENTO: { label: "Em andamento", colorScheme: "blue"   },
  AGUARDANDO:   { label: "Não Iniciado", colorScheme: "yellow" },
  AGUARDANDO_RH:{ label: "Aguardando RH",colorScheme: "orange" },
  CANCELADO:    { label: "Cancelado",    colorScheme: "red"    },
};

const progressColorScheme = (pct: number): string => {
  if (pct >= 100) return "green";
  if (pct > 0)    return "blue";
  return "gray";
};

/** Card do candidato — layout vertical (colunas de Kanban são estreitas).
 * Mesmas informações e ações de sempre, só reorganizadas verticalmente. */
const CandidateOnboardingCard = ({ candidate, onViewDetail, onCancel, onReactivate, isSelected, onToggleSelect, showHrProgress, whitelistEntry }: CandidateCardProps) => {
  const { user_id, name, email, position, completed_missions, total_missions, progress_pct, status, is_pj, is_cancelled } = candidate;
  const { permissions } = usePermissionsContext();
  const canViewSalary = permissions.includes(PERMISSIONS.RH.SALARY_VIEW);
  const effectiveIsPj = whitelistEntry?.is_pj ?? is_pj ?? false;
  const hrProgress = showHrProgress ? getHrProgress({ candidate, whitelist: whitelistEntry, canViewSalary, isPj: effectiveIsPj }) : null;
  const config = STATUS_CONFIG[status] ?? STATUS_CONFIG.AGUARDANDO;
  const completionDate = whitelistEntry?.onboarding_completed_at ?? candidate.completed_at;
  const insertedAt = whitelistEntry?.created_at ?? candidate.hr_created_at;

  const baseCardBg = useColorModeValue("white", "#0f2040");
  const selectedCardBg = useColorModeValue("blue.50", "whiteAlpha.200");
  const cancelledCardBg = useColorModeValue("red.50", "rgba(254,178,178,0.06)");
  const cardBg = isSelected ? selectedCardBg : is_cancelled ? cancelledCardBg : baseCardBg;

  const baseBorderColor = useColorModeValue(
    status === "CONCLUIDO"     ? "green.200"  :
    status === "AGUARDANDO_RH" ? "orange.200" :
    status === "CANCELADO"     ? "red.200"    : "gray.200",
    status === "CONCLUIDO"     ? "green.800"  :
    status === "AGUARDANDO_RH" ? "orange.800" :
    status === "CANCELADO"     ? "red.900"    : "whiteAlpha.100"
  );
  const selectedBorderColor = useColorModeValue("blue.400", "blue.300");
  const borderColor = isSelected ? selectedBorderColor : baseBorderColor;
  const progressTrackBg = useColorModeValue("gray.100", "whiteAlpha.100");
  const labelColor = useColorModeValue("gray.400", "gray.500");
  const subtleColor = useColorModeValue("gray.500", "gray.400");

  return (
    <Box
      bg={cardBg}
      borderRadius="md"
      borderWidth="1px"
      borderColor={borderColor}
      p={3}
      w="full"
      transition="all 0.2s"
      cursor={onToggleSelect ? "pointer" : "default"}
      _hover={onToggleSelect ? { shadow: "md", transform: "translateY(-1px)" } : undefined}
      onClick={() => onToggleSelect?.(user_id)}
      opacity={is_cancelled ? 0.75 : 1}
    >
      <Flex align="flex-start" gap={2} mb={2}>
        {onToggleSelect && (
          <Checkbox
            mt="2px"
            isChecked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              onToggleSelect(user_id);
            }}
            colorScheme="blue"
            flexShrink={0}
          />
        )}
        <UserAvatar size="sm" name={name} email={email} flexShrink={0} />
        <Box minW={0} flex={1}>
          <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>{name}</Text>
          <Text fontSize="xs" color={subtleColor} noOfLines={1}>{email}</Text>
        </Box>
        <HStack spacing={1} flexShrink={0} onClick={(e) => e.stopPropagation()}>
          <IconButton
            icon={<ViewIcon />}
            size="xs"
            variant="solid"
            colorScheme="blue"
            aria-label="Ver detalhes"
            onClick={(e) => {
              e.stopPropagation();
              onViewDetail?.(candidate);
            }}
          />

          {is_cancelled ? (
            <ChildrenInteractionBlocker
              requiredPermissions={[PERMISSIONS.RH.CANDIDATES_MANAGEMENT]}
              toastMessage="Você não tem permissão para reativar candidatos."
            >
              <Tooltip label="Reativar candidato" fontSize="xs" hasArrow>
                <IconButton
                  icon={<UserCheck size={13} />}
                  size="xs"
                  variant="outline"
                  colorScheme="green"
                  aria-label="Reativar candidato"
                  onClick={(e) => {
                    e.stopPropagation();
                    onReactivate?.(candidate);
                  }}
                />
              </Tooltip>
            </ChildrenInteractionBlocker>
          ) : (
            <ChildrenInteractionBlocker
              requiredPermissions={[PERMISSIONS.RH.CANDIDATES_MANAGEMENT]}
              toastMessage="Você não tem permissão para cancelar candidatos."
            >
              <Tooltip label="Cancelar candidato (desistência)" fontSize="xs" hasArrow>
                <IconButton
                  icon={<UserMinus size={13} />}
                  size="xs"
                  variant="outline"
                  colorScheme="red"
                  aria-label="Cancelar candidato"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCancel?.(candidate);
                  }}
                />
              </Tooltip>
            </ChildrenInteractionBlocker>
          )}
        </HStack>
      </Flex>

      <Flex direction="column" gap={1.5} mb={3} px={1}>
        <Tooltip label="Vaga" fontSize="xs" hasArrow placement="top">
          <HStack spacing={2} align="center" w="fit-content" maxW="100%">
            <Flex align="center" justify="center" w="16px" flexShrink={0}>
              <Briefcase size={14} color={useColorModeValue("#718096", "#A0AEC0")} />
            </Flex>
            <Text fontSize="xs" color={subtleColor} noOfLines={1}>
              {position || "Vaga não informada"}
            </Text>
          </HStack>
        </Tooltip>

        <Tooltip label="Recrutador(a) Responsável" fontSize="xs" hasArrow placement="top">
          <HStack spacing={2} align="center" w="fit-content" maxW="100%">
            <Flex align="center" justify="center" w="16px" flexShrink={0}>
              <UserRound size={14} color={useColorModeValue("#718096", "#A0AEC0")} />
            </Flex>
            <Text fontSize="xs" color={whitelistEntry?.recruiter_name || candidate.recruiter_name ? "blue.500" : "orange.500"} noOfLines={1}>
              {whitelistEntry?.recruiter_name || candidate.recruiter_name || "Sem recrutador"}
            </Text>
          </HStack>
        </Tooltip>

        {insertedAt && (
          <Tooltip label="Data de inserção no GG Hub" fontSize="xs" hasArrow placement="top">
            <HStack spacing={2} align="center" w="fit-content" maxW="100%">
              <Flex align="center" justify="center" w="16px" flexShrink={0}>
                <CalendarPlus size={14} color={useColorModeValue("#718096", "#A0AEC0")} />
              </Flex>
              <Text fontSize="xs" color={subtleColor} whiteSpace="nowrap">
                {formatSp(insertedAt, "dd/MM/yyyy HH:mm")}
              </Text>
            </HStack>
          </Tooltip>
        )}

        {completionDate && (
          <Tooltip label="Data de conclusão da trilha" fontSize="xs" hasArrow placement="top">
            <HStack spacing={2} align="center" w="fit-content" maxW="100%">
              <Flex align="center" justify="center" w="16px" flexShrink={0}>
                <CalendarCheck size={14} color={useColorModeValue("#38A169", "#48BB78")} />
              </Flex>
              <Text fontSize="xs" fontWeight="bold" color="green.500" whiteSpace="nowrap">
                {formatSp(completionDate, "dd/MM/yyyy HH:mm")}
              </Text>
            </HStack>
          </Tooltip>
        )}

        {showHrProgress && (
        <Tooltip
          label={whitelistEntry?.dp_finished ? "DP finalizado" : "DP não finalizado"}
          fontSize="xs"
          hasArrow
          placement="top"
        >
          <HStack spacing={2} align="center" w="fit-content" maxW="100%">
            <Flex align="center" justify="center" w="16px" flexShrink={0}>
              <CheckCircle2
                size={14}
                color={whitelistEntry?.dp_finished
                  ? useColorModeValue("#38A169", "#48BB78")
                  : useColorModeValue("#D69E2E", "#D69E2E")}
              />
            </Flex>
            <Text
              fontSize="xs"
              fontWeight="semibold"
              color={whitelistEntry?.dp_finished ? "green.500" : "orange.500"}
              noOfLines={1}
            >
              {whitelistEntry?.dp_finished ? "DP finalizado" : "DP pendente"}
            </Text>
          </HStack>
        </Tooltip>
        )}
      </Flex>

      <Flex align="center" gap={2} mb={3}>
        <Box flex={1}>
          {hrProgress ? (
            <Progress
              value={hrProgress.percent}
              colorScheme={hrProgress.percent >= 100 ? "green" : hrProgress.percent > 0 ? "purple" : "gray"}
              size="sm"
              borderRadius="full"
              bg={progressTrackBg}
            />
          ) : (
            <Progress
              value={progress_pct}
              colorScheme={is_cancelled ? "red" : progressColorScheme(progress_pct)}
              size="sm"
              borderRadius="full"
              bg={progressTrackBg}
            />
          )}
        </Box>
        <Text fontSize="xs" color={subtleColor} whiteSpace="nowrap">
          {hrProgress
            ? `${hrProgress.completed}/${hrProgress.total}`
            : `${completed_missions}/${total_missions}`}
        </Text>
      </Flex>

      <HStack spacing={1.5} wrap="wrap">
        {is_pj !== undefined && is_pj !== null && (
          <Badge colorScheme={is_pj ? "yellow" : "purple"} variant="subtle" fontSize="2xs">
            {is_pj ? "PJ" : "CLT"}
          </Badge>
        )}
        <Badge colorScheme={config.colorScheme} variant="subtle" fontSize="2xs">
          {config.label}
        </Badge>
      </HStack>
    </Box>
  );
};

export default CandidateOnboardingCard;
